import { useState } from "react";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import Reveal from "@Components/Reveal/Reveal";
import styles from "./ScholarshipFaq.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { AppState } from "@Store/index";
import { getScholarshipFaqData } from "constants/scholarshipConstants";

/**
 * FAQ accordion — Figma node 2102:3548. The first item renders open, as in the
 * design. One item open at a time.
 *
 * The questions are `getScholarshipFaqData` — the same nine the page this
 * replaces served (and what /scholarship shows on staging today), rather than a
 * v2-only set. They carry translations for every locale the site ships and they
 * are the copy the scholarship's search traffic already lands on, so the design
 * change should not quietly drop them.
 *
 * Every answer is in the markup whether or not its row is open, hidden with the
 * `hidden` attribute instead of being conditionally rendered. Conditional
 * rendering kept eight of the nine answers out of the server-rendered HTML
 * entirely, which is the text this page most wants indexed.
 */
const Faq = () => {
  const { t } = useTranslationHook();
  const nonce = useSelector(({ appData }: AppState) => appData.nonce);
  const [open, setOpen] = useState(0);

  const items = getScholarshipFaqData(t);

  // FAQPage rich result. Server-only, matching how MetaConfig emits its schema —
  // the crawler reads the SSR document, and re-emitting on the client would
  // duplicate the block on every route change.
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };

  return (
    <section className={styles.section}>
      {!__CLIENT__ && (
        <Helmet>
          <script type="application/ld+json" nonce={nonce}>
            {JSON.stringify(faqSchema)}
          </script>
        </Helmet>
      )}

      <div className={styles.inner}>
        <Reveal className={styles.headingBlock}>
          <h2 className={styles.heading}>{t("scholarship.v2.faq.heading")}</h2>
          <p className={styles.subheading}>{t("scholarship.v2.faq.subheading")}</p>
        </Reveal>

        <Reveal className={styles.list} delay={100}>
          {items.map((item, i) => {
            const isOpen = open === i;
            const panelId = `scholarship-faq-answer-${i}`;
            return (
              <div key={item.question} className={styles.item}>
                <button
                  type="button"
                  className={styles.trigger}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                >
                  {/* h3 under the section's h2 — these are the page's indexable
                      question headings, not decorative labels. */}
                  <h3 className={styles.question}>{item.question}</h3>
                  {/* Rotates to an × when open. */}
                  <span
                    className={`${styles.toggle} ${isOpen ? styles.toggleOpen : ""}`}
                    aria-hidden="true"
                  />
                </button>

                <p id={panelId} className={styles.answer} hidden={!isOpen}>
                  {item.answer}
                </p>
              </div>
            );
          })}
        </Reveal>
      </div>
    </section>
  );
};

export default wrapperHOC(Faq, {
  componentName: "Faq-ScholarshipV2",
  showForChina: true,
});
